import { router } from '@inertiajs/react';
import { Check } from 'lucide-react';
import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  formatEventWeekendRangeEs,
  getEventIanaTimezone,
} from '@/lib/timezone';
import type { HackerStatus } from '@/types';
import { extractGithubUsername } from './githubUsername';

interface WaitingListFormProps {
  publicId: string;
  statusData: HackerStatus;
  error: string | null;
}

export function WaitingListForm({
  publicId,
  statusData,
  error,
}: WaitingListFormProps) {
  const [isPending, setIsPending] = useState(false);

  const { hacker, submission, event, teamMembers } = statusData;
  const isPlural = submission.modality === 'team';
  const isFemale = hacker.gender === 'female';
  const weekendRange = formatEventWeekendRangeEs(
    event.startsAt,
    event.endsAt,
    getEventIanaTimezone(event.slug),
  );
  const weekendClause = weekendRange ? ` (${weekendRange})` : '';

  const handleJoin = () => {
    setIsPending(true);
    router.post(
      `/hacker/${publicId}/waiting-list`,
      { submissionId: submission.id },
      { onFinish: () => setIsPending(false) },
    );
  };

  const hasMultipleMembers = submission.isTeam && teamMembers.length > 1;

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h1 className="mb-2 font-bold font-title text-2xl text-primary sm:text-3xl md:text-4xl">
          <span className="bg-primary px-2 py-1 text-background">
            Lista de Espera
          </span>
        </h1>
        <p className="font-mono text-muted-foreground text-sm">{event.name}</p>
      </div>

      <div className="border-2 border-primary bg-background/80 p-6 backdrop-blur-sm sm:p-8">
        <div className="space-y-6">
          <div className="border-primary/20 border-b pb-4">
            <h2 className="mb-2 font-bold font-title text-primary text-xl">
              Hola {hacker.fullName.trim()}
              {hasMultipleMembers ? ' y equipo' : ''}
            </h2>
            <p className="font-mono text-primary/80 text-sm">
              Existe la posibilidad que equipos seleccionados cancelen su
              asistencia al evento a último minuto.
            </p>
          </div>

          {teamMembers.length > 0 && (
            <div>
              <h3 className="mb-3 font-bold font-title text-primary">
                {hasMultipleMembers ? 'Miembros del equipo:' : 'Participante:'}
              </h3>
              <div className="space-y-2">
                {teamMembers.map((member) => (
                  <div
                    key={member.id}
                    className="border-primary border-l-4 pl-3 font-mono text-primary/80 text-sm"
                  >
                    {member.fullName}
                    {member.github && (
                      <span className="ml-2 text-primary/60">
                        (@{extractGithubUsername(member.github)})
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-lg bg-muted p-4">
            <p className="mb-2 font-bold font-title text-primary text-sm">
              Confirmación:
            </p>
            <p className="font-mono text-sm">
              {isPlural
                ? `Al unirse a la lista de espera, el equipo confirma que todos sus miembros están dispuestos y disponibles para asistir el fin de semana completo${weekendClause} a ${event.name}.`
                : `Al unirte a la lista de espera, confirmas que estás ${isFemale ? 'dispuesta' : 'dispuesto'} y disponible para asistir el fin de semana completo${weekendClause} a ${event.name}.`}
            </p>
            <p className="mt-3 font-mono text-muted-foreground text-xs">
              {isPlural
                ? 'Si son seleccionados de la lista de espera, recibirán un correo electrónico con más información.'
                : `Si eres ${isFemale ? 'seleccionada' : 'seleccionado'} de la lista de espera, recibirás un correo electrónico con más información.`}
            </p>
          </div>

          {error && (
            <div className="rounded-lg border border-destructive bg-destructive/10 p-3">
              <p className="text-center font-mono text-destructive text-sm">
                {error}
              </p>
            </div>
          )}

          <div className="flex flex-col gap-3">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button className="w-full" size="lg" disabled={isPending}>
                  <Check className="mr-2 h-5 w-5" />
                  {isPlural
                    ? 'Sí, queremos unirnos a la lista de espera'
                    : 'Sí, quiero unirme a la lista de espera'}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    Confirmar Unión a Lista de Espera
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {isPlural ? (
                      `Al confirmar, el equipo se une a la lista de espera de ${event.name}. Todos los miembros confirman estar disponibles para asistir el fin de semana completo${weekendClause} si son seleccionados.`
                    ) : (
                      <>
                        Al confirmar, te unes a la lista de espera de{' '}
                        {event.name}. Confirmas estar disponible para asistir el
                        fin de semana completo{weekendClause} si eres{' '}
                        {isFemale ? 'seleccionada' : 'seleccionado'}.
                      </>
                    )}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={handleJoin} disabled={isPending}>
                    Confirmar
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>

      <div className="text-center">
        <a
          href={`/${event.slug}`}
          className="inline-block font-mono text-muted-foreground text-sm underline transition-colors hover:text-primary"
        >
          Volver al inicio
        </a>
      </div>
    </div>
  );
}
